import type { ComponentType } from 'react';
import { createBrowserRouter, Navigate, Outlet, type RouteObject } from 'react-router';
import { GuestLoading } from '../features/meetings/guest/GuestLoading';
import { PublicFormLoading } from '../features/public-form/PublicFormLoading';
import { AppShell } from './AppShell';
import { type ModuleDefinition, MODULES, useModuleAccess } from './modules';
import { AppLoading, RequireAuth } from './RequireAuth';
import { NotFound, RouteError } from './RouteError';
import { useSession } from './session';

/** Маршрути модуля доступні лише тим, кому модуль показано в меню (область доступу, useAccess). */
function ModuleGate({ module }: { module: ModuleDefinition }) {
  const { can } = useSession();
  const access = useModuleAccess(can);
  return access.get(module.key) ? <Outlet /> : <NotFound />;
}

/** Лінива сторінка: окремий фрагмент збірки для кожного розділу. */
const page = (load: () => Promise<{ default: ComponentType }>): Pick<RouteObject, 'lazy'> => ({
  lazy: async () => ({ Component: (await load()).default }),
});

/** Маршрути модулів хвилі 2 (src/features/<модуль>/routes.tsx) — підключаються автоматично. */
const moduleRoutes: RouteObject[] = MODULES.filter((m) => m.routes.length > 0).map((m) => ({
  path: m.basePath.replace(/^\//, ''),
  element: <ModuleGate module={m} />,
  children: m.routes,
}));

export const routes: RouteObject[] = [
  {
    path: '/login',
    ...page(() => import('../features/auth/LoginPage')),
    errorElement: <RouteError />,
    hydrateFallbackElement: <AppLoading />,
  },
  {
    // Публічна форма для клієнтів: без входу й без оболонки застосунку.
    path: '/f/:token',
    ...page(() => import('../features/public-form/PublicFormPage')),
    errorElement: <RouteError />,
    hydrateFallbackElement: <PublicFormLoading />,
  },
  {
    // Сторінка гостя відеозустрічі: персональне посилання без входу й без оболонки застосунку.
    path: '/meetings/guest/:token',
    ...page(() => import('../features/meetings/guest/GuestPage')),
    errorElement: <RouteError />,
    hydrateFallbackElement: <GuestLoading />,
  },
  {
    element: <RequireAuth />,
    errorElement: <RouteError />,
    hydrateFallbackElement: <AppLoading />,
    children: [
      {
        element: <AppShell />,
        errorElement: <RouteError />,
        children: [
          { index: true, element: <Navigate to="/my-work" replace /> },
          { path: 'my-work', ...page(() => import('../features/my-work/MyWorkPage')) },
          { path: 'inbox', ...page(() => import('../features/inbox/InboxPage')) },
          { path: 'projects', ...page(() => import('../features/projects/ProjectsPage')) },
          {
            path: 'projects/:projectId',
            ...page(() => import('../features/projects/ProjectPage')),
            children: [
              { index: true, ...page(() => import('../features/projects/ProjectDefaultView')) },
              { path: 'list', ...page(() => import('../features/projects/ListView')) },
              { path: 'board', ...page(() => import('../features/projects/BoardView')) },
              { path: 'calendar', ...page(() => import('../features/projects/CalendarView')) },
              { path: 'timeline', ...page(() => import('../features/projects/TimelineView')) },
              { path: 'settings', ...page(() => import('../features/projects/ProjectSettings')) },
            ],
          },
          { path: 'tasks/:taskId', ...page(() => import('../features/tasks/TaskPage')) },
          { path: 'search', ...page(() => import('../features/search/SearchPage')) },
          { path: 'knowledge/terms', ...page(() => import('../features/knowledge/TermsPage')) },
          { path: 'settings', ...page(() => import('../features/settings/SettingsPage')) },
          {
            path: 'admin',
            ...page(() => import('../features/admin/AdminLayout')),
            children: [
              { index: true, element: <Navigate to="users" replace /> },
              { path: 'users', ...page(() => import('../features/admin/UsersAdmin')) },
              { path: 'departments', ...page(() => import('../features/admin/DepartmentsAdmin')) },
              { path: 'teams', ...page(() => import('../features/admin/TeamsAdmin')) },
              { path: 'policy', ...page(() => import('../features/admin/PolicyAdmin')) },
              { path: 'tokens', ...page(() => import('../features/admin/TokensAdmin')) },
              { path: 'webhooks', ...page(() => import('../features/admin/WebhooksAdmin')) },
              { path: 'calendar', ...page(() => import('../features/admin/CalendarAdmin')) },
              { path: 'api', ...page(() => import('../features/admin/ApiAdmin')) },
              { path: 'overview', ...page(() => import('../features/admin/OverviewAdmin')) },
              { path: 'audit', ...page(() => import('../features/admin/AuditAdmin')) },
              { path: 'views', ...page(() => import('../features/admin/ViewsAdmin')) },
              { path: 'alerts', ...page(() => import('../features/admin/AlertsAdmin')) },
              { path: 'retention', ...page(() => import('../features/admin/RetentionAdmin')) },
              { path: 'exports', ...page(() => import('../features/admin/ExportsAdmin')) },
              { path: 'backups', ...page(() => import('../features/admin/BackupsAdmin')) },
            ],
          },
          ...moduleRoutes,
          { path: '*', element: <NotFound /> },
        ],
      },
    ],
  },
];

export const router = createBrowserRouter(routes);
